import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, UserPlus, Loader2, Trash2, Shield, ShieldCheck,
  Eye, BookOpen, Crown, Mail, ChevronDown,
} from 'lucide-react';
import { teamAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

const ROLES = [
  { value: 'admin', label: 'Admin', desc: 'Full access + team management', icon: ShieldCheck, color: 'text-violet-600' },
  { value: 'accountant', label: 'Accountant', desc: 'Upload, transactions, journal entries, reports', icon: BookOpen, color: 'text-blue-600' },
  { value: 'viewer', label: 'Viewer', desc: 'Read-only access to reports', icon: Eye, color: 'text-zinc-500' },
];

export default function TeamManagement() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('accountant');
  const [inviting, setInviting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [roleTarget, setRoleTarget] = useState(null);

  const isOwner = user?.role === 'owner';
  const isAdmin = user?.role === 'owner' || user?.role === 'admin';

  const loadData = useCallback(async () => {
    try {
      const [membersRes, invitesRes] = await Promise.all([
        teamAPI.listMembers(),
        isAdmin ? teamAPI.listInvitations() : Promise.resolve({ data: [] }),
      ]);
      setMembers(membersRes.data);
      setInvitations(invitesRes.data);
    } catch (e) {
      console.error('Failed to load team data:', e);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      await teamAPI.invite(inviteEmail.trim(), inviteRole);
      toast.success(`Invitation sent to ${inviteEmail}`);
      setInviteEmail('');
      await loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  const handleRevokeInvite = async (inviteId) => {
    try {
      await teamAPI.revokeInvitation(inviteId);
      toast.success('Invitation revoked');
      await loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to revoke');
    }
  };

  const handleUpdateRole = async () => {
    if (!roleTarget) return;
    try {
      await teamAPI.updateRole(roleTarget.id, roleTarget.newRole);
      toast.success(`Role updated to ${roleTarget.newRole}`);
      setRoleTarget(null);
      await loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleRemoveMember = async () => {
    if (!deleteTarget) return;
    try {
      await teamAPI.removeMember(deleteTarget.id);
      toast.success(`Removed ${deleteTarget.email}`);
      setDeleteTarget(null);
      await loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove member');
    }
  };

  const getRoleIcon = (role) => {
    if (role === 'owner') return <Crown className="w-4 h-4 text-amber-500" />;
    const r = ROLES.find(r => r.value === role);
    if (r) { const Icon = r.icon; return <Icon className={`w-4 h-4 ${r.color}`} />; }
    return <Eye className="w-4 h-4 text-zinc-400" />;
  };

  const getRoleBadge = (role) => {
    const colors = {
      owner: 'bg-amber-100 text-amber-800 border-amber-200',
      admin: 'bg-violet-100 text-violet-800 border-violet-200',
      accountant: 'bg-blue-100 text-blue-800 border-blue-200',
      viewer: 'bg-zinc-100 text-zinc-600 border-zinc-200',
    };
    return colors[role] || colors.viewer;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-32"><Loader2 className="w-6 h-6 animate-spin text-[#D94E18]" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="team-management">
      {/* Invite */}
      {isAdmin && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-[#D94E18]" /> Invite Team Member
            </CardTitle>
            <CardDescription>
              Send an invitation email. The invited person will join your organization when they register.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <Input
                  type="email"
                  placeholder="colleague@company.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  data-testid="invite-email-input"
                />
              </div>
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                className="h-9 rounded-md border border-zinc-200 px-3 text-sm w-full sm:w-40"
                data-testid="invite-role-select"
              >
                {ROLES.map(r => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
              <Button
                onClick={handleInvite}
                disabled={inviting || !inviteEmail.trim()}
                className="bg-[#D94E18] hover:bg-[#C2410C] text-white"
                data-testid="send-invite-btn"
              >
                {inviting ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Mail className="w-4 h-4 mr-1.5" />}
                Invite
              </Button>
            </div>

            {/* Pending invitations */}
            {invitations.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-medium text-zinc-500 mb-2">Pending Invitations</p>
                <div className="space-y-2">
                  {invitations.map(inv => (
                    <div key={inv.id} className="flex items-center justify-between p-2 bg-amber-50 border border-amber-100 rounded-lg" data-testid={`invitation-${inv.id}`}>
                      <div>
                        <p className="text-sm font-medium">{inv.email}</p>
                        <p className="text-xs text-zinc-400">Role: {inv.role} — Invited {new Date(inv.created_at).toLocaleDateString()}</p>
                      </div>
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => handleRevokeInvite(inv.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        data-testid={`revoke-invite-${inv.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Role descriptions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#D94E18]" /> Role Permissions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {ROLES.map(r => {
              const Icon = r.icon;
              return (
                <div key={r.value} className="p-3 rounded-lg border border-zinc-100 bg-zinc-50/50">
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className={`w-4 h-4 ${r.color}`} />
                    <span className="text-sm font-semibold">{r.label}</span>
                  </div>
                  <p className="text-xs text-zinc-500">{r.desc}</p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Members list */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="w-5 h-5 text-[#D94E18]" /> Team Members ({members.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="divide-y divide-zinc-100">
            {members.map(m => (
              <div key={m.id} className="flex items-center justify-between py-3 gap-3" data-testid={`team-member-${m.id}`}>
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="w-9 h-9 rounded-full bg-zinc-100 flex items-center justify-center flex-shrink-0">
                    {getRoleIcon(m.role)}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-zinc-900 truncate">
                      {m.name} {m.id === user?.id && <span className="text-zinc-400">(you)</span>}
                    </p>
                    <p className="text-xs text-zinc-400 truncate">{m.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Badge variant="outline" className={`text-xs ${getRoleBadge(m.role)}`}>
                    {m.role}
                  </Badge>
                  {isOwner && m.id !== user?.id && m.role !== 'owner' && (
                    <>
                      <select
                        value={m.role}
                        onChange={(e) => setRoleTarget({ id: m.id, email: m.email, newRole: e.target.value })}
                        className="h-7 rounded border border-zinc-200 px-1.5 text-xs"
                        data-testid={`role-select-${m.id}`}
                      >
                        {ROLES.map(r => (
                          <option key={r.value} value={r.value}>{r.label}</option>
                        ))}
                      </select>
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => setDeleteTarget(m)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 h-7 w-7 p-0"
                        data-testid={`remove-member-${m.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Role change confirmation */}
      <AlertDialog open={!!roleTarget} onOpenChange={() => setRoleTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change Role?</AlertDialogTitle>
            <AlertDialogDescription>
              Change {roleTarget?.email}'s role to <strong>{roleTarget?.newRole}</strong>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleUpdateRole} className="bg-[#D94E18]" data-testid="confirm-role-change-btn">
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Remove member confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">Remove Team Member?</AlertDialogTitle>
            <AlertDialogDescription>
              Remove {deleteTarget?.email} from the team? They will lose access to all organization data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemoveMember} className="bg-red-600 hover:bg-red-700" data-testid="confirm-remove-member-btn">
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
